import { useState, type FormEvent } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';

import { api } from '../lib/api';
import { canManageUsers, mutate, type StoreRole } from '../lib/mutate';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/cn';
import { formatDateTime } from '../lib/format';
import { EmptyState, ErrorNote, LoadMore, Panel, Spinner } from '../components/ui';
import { Field, inputClass, Modal } from '../components/Modal';

interface Member {
  userId: string;
  email: string;
  fullName: string;
  role: StoreRole;
  status: string;
  totpEnabled: boolean;
  lastLoginAt: string | null;
}

interface AuditEntry {
  at: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  actor: string;
  before: unknown;
  after: unknown;
}

export function Access(): React.JSX.Element {
  const { activeStore, user } = useAuth();
  const storeId = activeStore?.storeId;
  const queryClient = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // What became of the last invitation. Kept apart from `error`: a person added
  // whose email did not go is a success with a warning, not a failure.
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null);

  const isOwner = canManageUsers(activeStore?.role);
  const timeZone = activeStore?.timezone ?? 'Europe/London';

  const members = useQuery({
    queryKey: ['members', storeId],
    queryFn: () => api.get<{ members: Member[] }>(`/stores/${storeId}/users`),
    enabled: Boolean(storeId) && isOwner,
  });

  // Paginated. An audit trail exists to be complete, so a silent cap was the
  // least defensible of all of them.
  const audit = useInfiniteQuery({
    queryKey: ['audit', storeId],
    queryFn: ({ pageParam }) =>
      api.get<{ entries: AuditEntry[]; nextCursor: string | null }>(
        `/stores/${storeId}/audit?limit=50` +
          `${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(storeId),
  });

  const auditEntries = audit.data?.pages.flatMap((page) => page.entries) ?? [];

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['members', storeId] });
    void queryClient.invalidateQueries({ queryKey: ['audit', storeId] });
  };

  const invite = useMutation({
    mutationFn: (body: { email: string; fullName: string; role: StoreRole }) =>
      mutate.post<{ awaitingPassword: boolean; emailSent: boolean }>(
        `/stores/${storeId}/users`,
        body,
      ),
    onSuccess: (result, body) => {
      setInviting(false);
      setError(null);
      setNotice(
        !result.awaitingPassword
          ? { tone: 'ok', text: `${body.fullName} can now see this store.` }
          : result.emailSent
            ? {
                tone: 'ok',
                text: `Invitation sent to ${body.email}.`,
              }
            : {
                tone: 'warn',
                text: `${body.fullName} has been added, but the invitation email could not be sent. Use Resend invite beside their name to try again.`,
              },
      );
      refresh();
    },
    onError: (caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not invite.'),
  });

  const resendInvite = useMutation({
    mutationFn: (member: Member) =>
      mutate.post<{ emailSent: boolean }>(`/stores/${storeId}/users/${member.userId}/invitation`),
    onSuccess: (result, member) => {
      setError(null);
      setNotice(
        result.emailSent
          ? { tone: 'ok', text: `A new invitation is on its way to ${member.email}. Earlier links no longer work.` }
          : { tone: 'warn', text: `The invitation to ${member.email} could not be sent. Try again in a few minutes.` },
      );
      refresh();
    },
    onError: (caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not resend the invitation.'),
  });

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: StoreRole }) =>
      mutate.patch(`/stores/${storeId}/users/${userId}`, { role }),
    onSuccess: refresh,
    onError: (caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not change the role.'),
  });

  const revoke = useMutation({
    mutationFn: (userId: string) => mutate.delete(`/stores/${storeId}/users/${userId}`),
    onSuccess: refresh,
    onError: (caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not remove access.'),
  });

  return (
    <>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="qs-eyebrow">Access</p>
          <h1 className="text-lg font-medium tracking-tight">People and changes</h1>
        </div>
        {isOwner && (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setNotice(null);
              setInviting(true);
            }}
            className="flex items-center gap-1.5 rounded bg-accent px-2.5 py-1.5 text-xs font-medium text-ink-inverse hover:bg-accent-hover"
          >
            <UserPlus size={14} aria-hidden />
            Add someone
          </button>
        )}
      </header>

      {error && <ErrorNote error={new Error(error)} />}

      {notice && (
        <p
          role="status"
          className={cn(
            'qs-panel mb-3 px-3 py-2 text-xs',
            notice.tone === 'ok' ? 'text-up' : 'border-warn/30 bg-warn-soft text-warn',
          )}
        >
          {notice.text}
        </p>
      )}

      {isOwner ? (
        <Panel title="Who can see this store" className="mb-3">
          {members.isPending ? (
            <Spinner label="Loading people…" />
          ) : members.data?.members.length === 0 ? (
            <EmptyState>Nobody else has access.</EmptyState>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <caption className="sr-only">People with access to this store</caption>
                <thead>
                  <tr className="border-b border-rule">
                    <th className="qs-eyebrow py-2 pl-3 text-left">Person</th>
                    <th className="qs-eyebrow hidden py-2 text-left md:table-cell">Status</th>
                    <th className="qs-eyebrow hidden py-2 text-left lg:table-cell">Last seen</th>
                    <th className="qs-eyebrow py-2 text-left">Role</th>
                    <th className="qs-eyebrow py-2 pr-3 text-right">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {members.data?.members.map((member) => {
                    const isSelf = member.userId === user?.userId;

                    return (
                      <tr key={member.userId} className="border-b border-rule last:border-0">
                        <td className="py-1.5 pl-3">
                          <p className="text-xs wrap-anywhere">
                            {member.fullName}
                            {isSelf && <span className="text-ink-faint"> (you)</span>}
                          </p>
                          {/* `wrap-anywhere`, not `break-words`: an email is one
                              unbreakable token, so it sets the column's minimum
                              width and a long one pushed the role control and
                              Remove off the edge of a phone. Only this one also
                              shrinks that minimum — and unlike `break-all` it
                              breaks the address only where it has to. */}
                          <p className="text-2xs wrap-anywhere text-ink-faint">{member.email}</p>
                        </td>
                        <td className="hidden py-1.5 text-xs md:table-cell">
                          <span
                            className={cn(
                              'rounded border px-1.5 py-0.5 text-2xs',
                              member.status === 'active'
                                ? 'border-up/30 bg-up/5 text-up'
                                : 'border-warn/30 bg-warn-soft text-warn',
                            )}
                          >
                            {member.status === 'invited' ? 'awaiting password' : member.status}
                          </span>
                          {member.totpEnabled && (
                            <span className="ml-1 text-2xs text-ink-faint">2FA</span>
                          )}
                        </td>
                        <td className="qs-figure hidden py-1.5 text-2xs text-ink-muted lg:table-cell">
                          {member.lastLoginAt ? formatDateTime(member.lastLoginAt, timeZone) : '—'}
                        </td>
                        <td className="py-1.5">
                          <select
                            value={member.role}
                            aria-label={`Role for ${member.fullName}`}
                            onChange={(event) =>
                              changeRole.mutate({
                                userId: member.userId,
                                role: event.target.value as StoreRole,
                              })
                            }
                            className="rounded border border-rule bg-surface px-1.5 py-1 text-xs outline-none focus:border-accent"
                          >
                            <option value="owner">Owner</option>
                            <option value="manager">Manager</option>
                            <option value="viewer">Viewer</option>
                          </select>
                        </td>
                        <td className="py-1.5 pr-3 text-right">
                          <div className="flex flex-wrap justify-end gap-1">
                            {member.status === 'invited' && (
                              <button
                                type="button"
                                onClick={() => resendInvite.mutate(member)}
                                disabled={resendInvite.isPending}
                                aria-label={`Resend invite to ${member.fullName}`}
                                className="rounded border border-rule px-2 py-0.5 text-2xs text-ink-muted hover:border-accent hover:text-accent disabled:opacity-60"
                              >
                                Resend invite
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => revoke.mutate(member.userId)}
                              className="rounded border border-rule px-2 py-0.5 text-2xs text-ink-muted hover:border-down hover:text-down"
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="border-t border-rule px-3 py-2 text-2xs text-ink-faint">
            Changing a role or removing access signs that person out straight away.
          </p>
        </Panel>
      ) : (
        <p className="qs-panel mb-3 p-3 text-xs text-ink-muted">
          Only an owner can manage who has access to this store.
        </p>
      )}

      {/* Visible to every grant holder. An audit trail nobody can read is not
          an audit trail. */}
      <Panel title="What the cloud has changed">
        {audit.isPending ? (
          <Spinner label="Loading changes…" />
        ) : auditEntries.length === 0 ? (
          <EmptyState>Nothing has been changed from here yet.</EmptyState>
        ) : (
          <ol className="divide-y divide-rule">
            {auditEntries.map((entry, index) => (
              <li key={`${entry.at}-${index}`} className="px-3 py-2">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="qs-figure text-2xs text-ink-faint">
                    {formatDateTime(entry.at, timeZone)}
                  </span>
                  <span className="text-xs">{describe(entry)}</span>
                  <span className="text-2xs text-ink-faint">by {entry.actor}</span>
                </div>
                {entry.before !== null && entry.after !== null && (
                  /* Mono, but not `qs-figure`: that class refuses to wrap, which
                     is right for a currency column and wrong here — a pricing
                     change lists four fields twice over, and on a phone the
                     panel simply clipped it, so the "after" half of the change
                     an audit trail exists to record was off the edge unread. */
                  <p className="mt-0.5 font-mono text-2xs break-words text-ink-muted">
                    {summarise(entry.before)} → {summarise(entry.after)}
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}

        <LoadMore
          shown={auditEntries.length}
          hasMore={Boolean(audit.hasNextPage)}
          isLoading={audit.isFetchingNextPage}
          onLoad={() => void audit.fetchNextPage()}
          noun="changes shown"
        />
      </Panel>

      <Modal open={inviting} title="Add someone to this store" onClose={() => setInviting(false)}>
        <form
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            invite.mutate({
              email: String(form.get('email')),
              fullName: String(form.get('fullName')),
              role: String(form.get('role')) as StoreRole,
            });
          }}
        >
          <Field label="Email" htmlFor="email">
            <input id="email" name="email" type="email" className={inputClass} required />
          </Field>
          <Field label="Full name" htmlFor="fullName">
            <input id="fullName" name="fullName" className={inputClass} required />
          </Field>
          <Field
            label="Role"
            htmlFor="role"
            hint="Viewers can read and use Genie. Managers can also edit the catalogue, promotions and credit accounts. Owners can additionally manage access."
          >
            <select id="role" name="role" className={inputClass} defaultValue="viewer">
              <option value="viewer">Viewer</option>
              <option value="manager">Manager</option>
              <option value="owner">Owner</option>
            </select>
          </Field>

          <p className="mb-3 rounded border border-rule bg-sunken px-2.5 py-2 text-2xs text-ink-muted">
            New people get an email with a link to choose their own password, so nobody else
            ever knows it. Someone who already has an account is told they can now see this
            store.
          </p>

          {error && (
            <p role="alert" className="mb-3 text-xs text-down">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setInviting(false)}
              className="rounded border border-rule px-3 py-1.5 text-xs text-ink-muted hover:text-ink"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={invite.isPending}
              className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-ink-inverse hover:bg-accent-hover disabled:opacity-60"
            >
              {invite.isPending ? 'Sending…' : 'Add and send invite'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

/** Turns a dotted action into something readable without inventing detail. */
const ACTIONS: Record<string, string> = {
  'product.pricing.updated': 'changed a product’s price or thresholds',
  'product.classification.updated': 'changed a product’s department or VAT',
  'category.created': 'added a department',
  'credit.customer.created': 'added a credit customer',
  'credit.customer.updated': 'edited a credit customer',
  'credit.supplier.created': 'added a supplier',
  'credit.supplier.updated': 'edited a supplier',
  'promotion.activated': 'activated a promotion',
  'promotion.deactivated': 'deactivated a promotion',
  'access.user.invited': 'invited someone',
  'access.invite.resent': 'resent an invitation',
  'access.grant.added': 'gave someone access',
  'access.role.changed': 'changed someone’s role',
  'access.revoked': 'removed someone’s access',
};

function describe(entry: AuditEntry): string {
  const verb = ACTIONS[entry.action] ?? entry.action;
  return entry.entityId ? `${verb} (#${entry.entityId})` : verb;
}

function summarise(value: unknown): string {
  if (value === null || typeof value !== 'object') return String(value);
  return Object.entries(value as Record<string, unknown>)
    .map(([key, val]) => `${key}: ${String(val)}`)
    .join(', ');
}
